/* eslint-disable no-param-reassign */
import React, {
    useContext, useCallback, useEffect, useRef
} from 'react';
import _ from 'lodash';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLTagManagerService } from 'ml-portals-services-react'
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { convertToSentenceCase } from 'ml-portals-util-js';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import AdditionalInterestInputComponent from '../AdditionalInterestInputComponent/AdditionalInterestInputComponent';
import messages from './AdditionalInterestListComponent.messages';
import metadata from './AdditionalInterestListComponent.metadata.json5';
import styles from './AdditionalInterestListComponent.module.scss';

function AdditionalInterestListComponent(props) {
    const { authHeader } = useAuthentication();
    const {
        model: additionalInterestVM,
        submissionVM,
        endorsementVM,
        id,
        onValueChange,
        path,
        updateViewModel,
        initialPotentialContact,
        showValidationErrors,
        onValidate,
        isHOLine,
        isRenters,
        coveragePartTypeML,
        isPersonalUmbrella,
        potentialContactsLoading,
        policyChangePaymentMethod,
        currentStepName,
        additionalInterestAvailableCandidatesML,
        financeCategory
    } = props;
    const { onValidate: setComponentValidation } = useValidation(id);
    const {
        clearErrorAlerts,
        addErrorAlert
    } = MLNotificationBlockUtil();
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { MLRatingPaymentMethods } = appConfig;

    const isGPA = appConfig.portalCode === 'GPA';
    const isQB = appConfig.portalCode === 'QB';

    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);

    const getSubtype = useCallback(() => {
        if (isPersonalUmbrella) {
            return 'PUPLineAddlInterest_ML';
        }
        if (!isHOLine) {
            return 'PAVhcleAddlInterest';
        }
        return 'HOPDwellAddlInterest';
    }, [isHOLine, isPersonalUmbrella]);

    const onAddAdditionalParty = useCallback(() => {
        const newInterestInitialVal = {
            subtype: getSubtype(),
            policyAdditionalInterest: {
                accountHolder: false,
                skipPrivacyDetails_ML: true,
                primaryAddress: {}
            },
            publicID: 'other'
        };

        // initialize additional interest VM
        additionalInterestVM.value = additionalInterestVM.value || [];
        const additionalInterestDTO = viewModelService.create(newInterestInitialVal, 'pc', 'edge.capabilities.policyjob.lob.AdditionalInterestDTO');
        additionalInterestVM.pushElement(additionalInterestDTO);
        updateViewModel();

        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Add Interested Party`);
    }, [additionalInterestVM, getSubtype, updateViewModel, viewModelService]);

    const getInterestOfType = useCallback((type) => {
        const additionalInterestList = _.get(additionalInterestVM, 'value', []);
        return additionalInterestList.find((interest) => {
            return interest.type === type;
        });
    }, [additionalInterestVM]);

    const getInterestOfTypeVM = useCallback((type) => {
        const interest = getInterestOfType(type);
        if (interest) {
            const idx = additionalInterestVM.value.indexOf(interest);
            if (idx > -1) {
                return additionalInterestVM.getElement(idx);
            }
        }
        return null;
    }, [additionalInterestVM, getInterestOfType]);

    const replaceInterestOfType = useCallback((type, newType) => {
        const interestWithTypeToReplace = getInterestOfTypeVM(type);
        if (interestWithTypeToReplace && interestWithTypeToReplace.value) {
            interestWithTypeToReplace.type.value = newType;
        }
    }, [getInterestOfTypeVM]);

    const onRemove = useCallback((interest) => {
        if (interest.type && interest.type.value) {
            switch (interest.type.value.code) {
                case 'FIRSTMORTGAGEE':
                {
                    const second = getInterestOfTypeVM('SECONDMORTGAGEE');
                    let secondType;
                    if (second && second.value) {
                        secondType = _.clone(second.type.value);
                    }
                    replaceInterestOfType('SECONDMORTGAGEE', interest.type.value);
                    if (secondType) {
                        replaceInterestOfType('THIRDMORTGAGEE', secondType);
                    }
                    break;
                }
                case 'SECONDMORTGAGEE':
                    replaceInterestOfType('THIRDMORTGAGEE', interest.type.value);
                    break;
                default:
                    break;
            }
        }
    }, [getInterestOfTypeVM, replaceInterestOfType]);

    const getAddlInterestsWithoutFirstMortgagee = useCallback(() => {
        const additionalInterests = _.get(additionalInterestVM, 'children', []);
        if(isHOLine) {
            const filteredInterests = _.filter(additionalInterests, (interest) => {
                const type = interest.type && interest.type.value ? interest.type.value.code : '';
                return ['FIRSTMORTGAGEE'].indexOf(type) === -1;
            });
            return filteredInterests;
        }
        return additionalInterests;
    }, [additionalInterestVM, isHOLine, additionalInterestVM.value]);

    const getIndexFromAdditionalInterestsList = useCallback((interest) => {
        const addlInterests = _.get(additionalInterestVM, 'children', []);
        const interestIndex = addlInterests.indexOf(interest);
        return interestIndex;
    }, [additionalInterestVM]);

    const getTypeName = useCallback((code) => {
        const additionalInterestTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('AdditionalInterestType');
        return additionalInterestTypeList.getCode(code).name;
    }, [viewModelService.productMetadata]);

    const getContactDetails = (item) => {
        if (item.policyAdditionalInterest.subtype === 'Company') {
            return item.policyAdditionalInterest.contactName ? item.policyAdditionalInterest.contactName : undefined;
        }
        return item.policyAdditionalInterest.firstName && item.policyAdditionalInterest.lastName ?
            `${item.policyAdditionalInterest.firstName} ${item.policyAdditionalInterest.lastName}` : undefined;
    };

    const updateLeftNavPane = useCallback((interest) => {
        const dynamicSteps = MLNavigationStepsTrackingService.getData() ? MLNavigationStepsTrackingService.getData().dynamicSteps : undefined;
        if (interest.value.exists) { // add a remove entry to the navigation step only if item was already saved
            const interestType = interest.type.value ? getTypeName(interest.type.value.code) : undefined;
            const nameOfType = interestType ? translator({
                id: interestType,
                defaultMessage: interestType
            }) : '';

            const contactDetails = getContactDetails(interest.value);
            // remove any edit log made together with the remove
            MLNavigationStepsTrackingService.removeFromDynamicSteps(translator(messages.editItem, { item: nameOfType }), true, currentStepName, authHeader);
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.removeItem, { item: nameOfType }), true, currentStepName, authHeader);

            MLNavigationStepsTrackingService.completeCurrentStep(`${contactDetails}`, authHeader);
        } else if (dynamicSteps && dynamicSteps.length > 0 && dynamicSteps[dynamicSteps.length - 1].isInProgress) {
            MLNavigationStepsTrackingService.removeFromDynamicSteps(dynamicSteps[dynamicSteps.length - 1].displayName, false, currentStepName, authHeader);
        }
    }, [authHeader, currentStepName, getTypeName, translator]);

    const removeAdditionalInterest = useCallback((removeIndex) => {
        const indexToRemove = removeIndex - 1;

        const addlInterestsWithoutFirstMortgageeVM = getAddlInterestsWithoutFirstMortgagee();
        const additionalInterestToRemove = addlInterestsWithoutFirstMortgageeVM[indexToRemove];

        const additionalInterestList = _.get(additionalInterestVM, 'value');
        const initialAddtlListLength = additionalInterestList.length;

        const idxToRemove = additionalInterestList.indexOf(additionalInterestToRemove.value);
        if (idxToRemove > -1) {
            updateLeftNavPane(additionalInterestToRemove);
            onRemove({ interest: additionalInterestToRemove });
            additionalInterestList.forEach((elem, idx) => {
                if (idx > idxToRemove) {
                    elem.ignoreEdit = true;
                }
            });
            MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Interested Party`);
            additionalInterestList.splice(idxToRemove, 1);
        }

        updateViewModel();
        onValidate(true, `additionalInterestInput${initialAddtlListLength - 1}`);
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Remove Interested Party`);
    }, [getAddlInterestsWithoutFirstMortgagee, additionalInterestVM, updateViewModel, onValidate, updateLeftNavPane, onRemove]);

    const removeEntityExtAdditionalInterest = useCallback(() => {
        const additionalInterestList = _.get(additionalInterestVM, 'value', []);

        const idxToRemove = additionalInterestList.findIndex(
            item => item && item.type === 'entity_Ext'
        );

        if (idxToRemove > -1) {
            additionalInterestList.splice(idxToRemove, 1);
            updateViewModel();
        }
    }, [additionalInterestVM, updateViewModel]);
    
    const previousFinanceCategoryRef = useRef(financeCategory);
 
    useEffect(() => {
        if(isGPA && isModernProduct && isHOLine) {
            removeEntityExtAdditionalInterest()
        }
        if (!isQB) {
            return
        }

        const previousFinanceCategory = previousFinanceCategoryRef.current;
 
        const wasLoanOrLease = isModernProduct ? (previousFinanceCategory === 'financed') : (previousFinanceCategory === 'leased' || previousFinanceCategory === 'financed')
 
        const isLoanOrLease = isModernProduct ? (financeCategory === 'financed') : (financeCategory === 'leased' || financeCategory === 'financed')
 
        const additionalInterests = _.get(additionalInterestVM, 'value', []);
 
        if (isLoanOrLease && additionalInterests.length === 0) {
            onAddAdditionalParty();
        }
 
        if (
            wasLoanOrLease &&
            !isLoanOrLease &&
            additionalInterests.length > 0
        ) {
            removeAdditionalInterest(1);
        }
 
        previousFinanceCategoryRef.current = financeCategory;
    }, [
        financeCategory,
        additionalInterestVM,
        onAddAdditionalParty,
        removeAdditionalInterest
    ]);
    
    const getSelectedInterestTypes = useCallback(() => {
        const interestList = _.get(additionalInterestVM, 'value');
        return new Set(interestList.map((interest) => interest.type));
    }, [additionalInterestVM]);

    const hasMortgage = useCallback(() => {
        return getInterestOfType('FIRSTMORTGAGEE');
    }, [getInterestOfType]);

    const getMortgage = useCallback(() => {
        return getInterestOfTypeVM('FIRSTMORTGAGEE');
    }, [getInterestOfTypeVM]);

    const hasReturnedDefaultRef = useRef(false);
    const previousSelectedFinanceCategoryRef = useRef(financeCategory);

    const getDefaultInterestedPartyType = useCallback(() => {
        if (previousSelectedFinanceCategoryRef.current !== financeCategory) {
            previousSelectedFinanceCategoryRef.current = financeCategory;
            hasReturnedDefaultRef.current = false;
        }

        if (hasReturnedDefaultRef.current) {
            return undefined;
        }

        let defaultValue;

        if (financeCategory === 'leased') {
            defaultValue = isModernProduct ? undefined : 'LEASINGCOMPANY';
        } else if (financeCategory === 'financed') {
            defaultValue = 'LIEN';
        }

        if (defaultValue) {
            hasReturnedDefaultRef.current = true;
        }

        return defaultValue;
    }, [financeCategory, isModernProduct]);

    const addMortgage = useCallback(() => {
        clearErrorAlerts();
        const newInterestInitialVal = {
            type: 'FIRSTMORTGAGEE',
            subtype: getSubtype(),
            policyAdditionalInterest: {
                subtype: 'Company',
                skipPrivacyDetails_ML: true,
                primaryAddress: {}
            },
            publicID: 'other'
        };

        // initialize additional interest VM
        additionalInterestVM.value = additionalInterestVM.value || [];
        const additionalInterestDTO = viewModelService.create(newInterestInitialVal, 'pc', 'edge.capabilities.policyjob.lob.AdditionalInterestDTO');
        additionalInterestVM.pushElement(additionalInterestDTO);
        updateViewModel();
    }, [additionalInterestVM, clearErrorAlerts, getSubtype, updateViewModel, viewModelService]);

    const updateMortgageLeftNavPane = useCallback((mortgage) => {
        if (mortgage.value.exists) { // add a remove entry to the navigation step only if item was already saved
            const mortgageType = mortgage.type.value ? getTypeName(mortgage.type.value.code) : undefined;
            const nameOfType = mortgageType ? translator({
                id: mortgageType,
                defaultMessage: mortgageType
            }) : '';

            const contactDetails = getContactDetails(mortgage.value);
            // remove any edit log made together with the remove
            MLNavigationStepsTrackingService.removeFromDynamicSteps(translator(messages.editItem, {
                item: contactDetails ? `${nameOfType} ${contactDetails}` : `${nameOfType}`}), true, currentStepName, authHeader);
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.removeItem, {
                item: contactDetails ? `${nameOfType} ${contactDetails}` : `${nameOfType}`}), true, currentStepName, authHeader);
        }
    }, [authHeader, currentStepName, getTypeName, translator]);

    const removeMortgage = useCallback(() => {
        if (policyChangePaymentMethod === MLRatingPaymentMethods.MORTGAGEE) {
            addErrorAlert(translator(messages.mortgagePaymentError), 'noMortgageeForPayment');
        }
        
        const mortgageToRemove = getInterestOfTypeVM('FIRSTMORTGAGEE');

        if (mortgageToRemove && mortgageToRemove.value) {
            updateMortgageLeftNavPane(mortgageToRemove);
            const idx = additionalInterestVM.value.indexOf(mortgageToRemove.value);
            if (idx > -1) {
                onRemove(mortgageToRemove);
                additionalInterestVM.value.splice(idx, 1);
                updateViewModel();
            }
        }

        onValidate(true, 'hoMortgageAdditionalInterestInput')
    }, [MLRatingPaymentMethods.MORTGAGEE, addErrorAlert, additionalInterestVM.value, getInterestOfTypeVM, onRemove, onValidate, policyChangePaymentMethod, translator, updateMortgageLeftNavPane, updateViewModel]);

    const handleValueChange = useCallback((value) => {
        if (value) {
            addMortgage();
        } else {
            const mortgageToRemove = getInterestOfTypeVM('FIRSTMORTGAGEE');
            const type =  mortgageToRemove?.value?.publicID;
            // Checking if valid or not 
            if(type === 'other' &&  ( !mortgageToRemove.aspects.valid || !mortgageToRemove.aspects.subtreeValid) ){
                //creating mock primaryAddress to  additional interest VM
               const tempAddress = {"state":"AZ","country":"US",
                                    "displayName":"PO BOX 78420, Phoenix, AZ 85062",
                                    "publicID":"pc_dev_ml:4864","city":"Phoenix",
                                    "addressLine1":"PO BOX 78420","postalCode":"85062",
                                    "addressType":"business"};
                mortgageToRemove.value.policyAdditionalInterest.primaryAddress = tempAddress;
                updateViewModel();
                //removing VM aftrer update VM 
                setTimeout(()=>{
                    removeMortgage();
                }, 100);
            }else{
                removeMortgage();
            }
        }
    }, [addMortgage, removeMortgage]);

    const generateOverrides = useCallback(() => {
        const additionalInterests = getAddlInterestsWithoutFirstMortgagee();

        const overrides = additionalInterests.map((interest, index) => {
            return {
                [`additionalInterestInput${index}`]: {
                    addtlInterestVM: interest,
                    removeAdditionalInterest,
                    index: (index + 1),
                    path: `${path}.additionalInterests.children[${getIndexFromAdditionalInterestsList(interest)}]`,
                    onValueChange,
                    getSelectedInterestTypes,
                    updateViewModel,
                    initialPotentialContact,
                    showValidationErrors,
                    onValidate,
                    currentStepName,
                    coveragePartTypeML,
                    submissionVM,
                    endorsementVM,
                    subtype: getSubtype(),
                    additionalInterestAvailableCandidatesML,
                    defaultInterestPartytype : getDefaultInterestedPartyType()
                }
            };
        });

        return Object.assign({}, ...overrides);
    }, [getAddlInterestsWithoutFirstMortgagee, removeAdditionalInterest, path, getIndexFromAdditionalInterestsList, onValueChange, getSelectedInterestTypes, updateViewModel, initialPotentialContact, showValidationErrors, onValidate, currentStepName, submissionVM, endorsementVM, getSubtype, additionalInterestAvailableCandidatesML]);

    const generateHOMortgageInputOverrides = () => {
        if (hasMortgage() !== undefined) {
            const currentMortgage = getMortgage();
            const currentIndex = additionalInterestVM.children.indexOf(currentMortgage);

            return {
                hoMortgageAdditionalInterestInput: {
                    addtlInterestVM: currentMortgage,
                    removeAdditionalInterest,
                    index: (currentIndex + 1),
                    path: `${path}.additionalInterests.children.${currentIndex}`,
                    onValueChange,
                    getSelectedInterestTypes,
                    updateViewModel,
                    initialPotentialContact,
                    showValidationErrors,
                    onValidate,
                    displayInterestedPartyTypeSelection: false,
                    display: false,
                    currentStepName,
                    subtype: getSubtype(),
                    additionalInterestAvailableCandidatesML
                }
            };
        }
        return {};
    };

    const getAddtionalInterestDesc = () => {
        if (isRenters) {
            return translator(messages.rentersAdditionalInterestDesc);
        }
        if (isHOLine) {
            return translator(messages.hoAdditionalInterestDesc);
        }
        if (isPersonalUmbrella) {
            return translator(messages.pupAdditionalInterestDesc);
        }
        return translator(messages.paAadditionalInterestDesc);
    };

    const overrideProps = {
        additionalInterestIterable: {
            data: getAddlInterestsWithoutFirstMortgagee()
        },
        hoAdditionalInterestListComponent: {
            visible: isHOLine
        },
        additionalInterestDesc: {
            content: getAddtionalInterestDesc()
        },
        addAdditionalPartyButton: {
            onClick: onAddAdditionalParty,
            type: isGPA ? 'outlined' : 'filled',
            className: isQB ? 'vertical-spacing-40 addInterestedPartyButton accentButton widthSmall' : 'vertical-spacing-40 addInterestedPartyButton',
            content: isQB ? convertToSentenceCase(translator(messages.addInterestedParty)) : translator(messages.addInterestedParty)
        },
        mortgageHeader: {
            className: (hasMortgage() === undefined) ? 'vertical-spacing-40 subsection-header' : 'subsection-header'
        },
        removeMortgageLink: {
            onClick: removeMortgage,
            visible: hasMortgage() !== undefined
        },
        mortgageRadioButton: {
            onValueChange: handleValueChange,
            value: hasMortgage() !== undefined,
            className: isQB ? "radioButtonQnB" : ""
        },
        hasMortgageRowDiv: {
            visible: hasMortgage() !== undefined
        },
        hoMortgageHR: {
            visible: (additionalInterestVM.children.length >= 2 && !!hasMortgage()) || (additionalInterestVM.children.length >= 1 && !hasMortgage())
        },
        hoNonRentersSection: {
            visible: !isRenters
        },
        hasMortgageContactsLoadingDiv: {
            visible: potentialContactsLoading
        },
        hasMortgageAdditionalInterestsInputDiv: {
            visible: !potentialContactsLoading
        },
        hasMortgageColDiv0: {
            className: isQB ? "mortgageFieldAlignments" : "col-md-8"
        },
        hasMortgagePopoverDiv: {
            className: isQB ? "mortgageFieldAlignments" : "ml-popover ml-popover-basic pointer-left clearfix"
        },
        hasMortgageColDiv1: {
            className: isQB ? "mortgageFieldAlignments" : "col-md-12"
        },
        ...generateHOMortgageInputOverrides(),
        ...generateOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            additionalinterestinputcomponent: AdditionalInterestInputComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={styles}
            overrideProps={overrideProps}
            model={additionalInterestVM}
            onValueChange={updateViewModel}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
        />
    );
}

AdditionalInterestListComponent.propTypes = {
    model: PropTypes.shape({}).isRequired,
    addressVM: PropTypes.shape({}),
    id: PropTypes.string.isRequired,
    onValueChange: PropTypes.func.isRequired,
    path: PropTypes.string.isRequired,
    updateViewModel: PropTypes.func.isRequired,
    initialPotentialContact: PropTypes.shape({}),
    showValidationErrors: PropTypes.bool.isRequired,
    onValidate: PropTypes.func.isRequired,
    isHOLine: PropTypes.bool,
    isRenters: PropTypes.bool,
    coveragePartTypeML:PropTypes.string,
    isPersonalUmbrella: PropTypes.bool,
    potentialContactsLoading: PropTypes.bool,
    policyChangePaymentMethod: PropTypes.string,
    currentStepName: PropTypes.string,
    submissionVM: PropTypes.shape({}),
    endorsementVM: PropTypes.shape({}),
};

AdditionalInterestListComponent.defaultProps = {
    isHOLine: false,
    isRenters: false,
    isPersonalUmbrella: false,
    initialPotentialContact: {},
    potentialContactsLoading: false,
    policyChangePaymentMethod: '',
    submissionVM: undefined,
    endorsementVM: undefined
};

export default AdditionalInterestListComponent;
